import { useState } from "react"
import { Box } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { faUtensils } from "@fortawesome/pro-solid-svg-icons"
import { ErrorBoundary } from "react-error-boundary"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { tailwindPalette } from "../../../../../../theme/tailwindPalette"
import { EmptyContent, EmptyContentVariant } from "../../../../../../../shared/components/skeleton/EmptyContent"
import { DatePeriod } from "../../../../../../../shared/components/filters/selectDatePeriodFilter/SelectDatePeriodFilter"
import { PurchaseOrder } from "../../../weekMenuTypes"
import { UserPurchases } from "../../../../upcomingOrders/purchases/pages/UserPurchases"
import { PurchaseOrderTypeFilter } from "../../../../upcomingOrders/purchases/purchasesApi"

import { UserDashboardErrorFallbackCard } from "../../UserDashboardErrorFallbackCard"
import { useListPagination } from "../../../../../../../shared/hooks/useListPagination"
import { MeetingListPagination } from "../../../../../../serviceCenter/report/home/components/meetigs/MeetingListPagination"
import { UpcomingOrdersPanel } from "../UpcomingOrdersPanel"
import { UpcomingOrdersModal } from "../UpcomingOrdersModal"
import { PurchaseRow } from "./PurchaseRow"

const useStyles = makeStyles()((theme) => ({
    rowsContainer: {
        display: "flex",
        flexDirection: "column",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    errorCard: {
        backgroundColor: "transparent",
        boxShadow: "none",
    },
    errorContent: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
    },
}))

type CanteenPurchasesPanelProps = Readonly<{
    purchases: ReadonlyArray<PurchaseOrder>
    period: DatePeriod
}>

export function CanteenPurchasesPanel({ purchases, period }: CanteenPurchasesPanelProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const { pagedItems, pagination } = useListPagination(purchases)

    return (
        <>
            <UpcomingOrdersPanel
                icon={faUtensils}
                iconBackgroundColor={tailwindPalette.green[200]}
                iconColor={tailwindPalette.green[900]}
                titleId="user-home-upcoming-canteen-purchases"
                titleFallback="Canteen purchases"
                counter={pagination.totalItems}
                onShowAll={() => setIsModalOpen(true)}
                collapseAriaLabel={l10n.getString("user-home-canteen-purchases-collapse")}
                expandAriaLabel={l10n.getString("user-home-canteen-purchases-expand")}
                footer={pagination.totalItems > 0 ? <MeetingListPagination {...pagination} /> : undefined}
            >
                {!pagination.totalItems ? (
                    <LocalizedStrict id="user-home-upcoming-canteen-purchases-no-purchases" attrs={{ title: true }}>
                        <EmptyContent
                            variant={EmptyContentVariant.SINGLE_LEFT}
                            title="No canteen purchases in period"
                        />
                    </LocalizedStrict>
                ) : (
                    <Box className={classes.rowsContainer}>
                        {pagedItems.map((purchase) => (
                            <ErrorBoundary
                                key={purchase.id}
                                fallback={
                                    <UserDashboardErrorFallbackCard
                                        classNames={classes.errorCard}
                                        contentClassNames={classes.errorContent}
                                    />
                                }
                            >
                                <PurchaseRow purchase={purchase} purchases={purchases} />
                            </ErrorBoundary>
                        ))}
                    </Box>
                )}
            </UpcomingOrdersPanel>
            {isModalOpen && (
                <LocalizedStrict id="user-home-upcoming-canteen-purchases-modal-title" attrs={{ title: true }}>
                    <UpcomingOrdersModal open onClose={() => setIsModalOpen(false)} title="Canteen purchases">
                        <UserPurchases orderType={PurchaseOrderTypeFilter.LUNCH} initialPeriod={period} />
                    </UpcomingOrdersModal>
                </LocalizedStrict>
            )}
        </>
    )
}
